import React, {useState} from 'react'
import {Modal, Button} from 'react-bootstrap'
import {Formik, Form, Field} from 'formik'
import * as Yup from 'yup'
import {KTIcon} from '../../../../../_websoft/helpers'
import {
    getDostupniDopoviPoSifri,
    getDostupniPrivremeniDopovi
} from "../../../../api/paneliDop";
import DopLanguageButtons from "../../dop-wizzard/components/DopLanguageButtons";

interface Props {
    show: boolean
    onClose: () => void
}

type DopItem = {
    Id: number
    izvor: 'standard' | 'privremeni'
    [key: string]: any
}

// ✅ CONDITIONAL VALIDATION
const validationSchema = Yup.object().test(
    'nalog-or-sifra',
    'Unesite ili broj naloga (i stavku) ili šifru',
    function (values: any) {
        const {brojNaloga, sifraArtikla} = values

        if (brojNaloga && brojNaloga.trim() !== '') return true
        if (sifraArtikla && sifraArtikla.trim() !== '') return true

        return false
    }
)

const IzvjestajiModal: React.FC<Props> = ({show, onClose}) => {

    const [results, setResults] = useState<DopItem[]>([])
    const [loading, setLoading] = useState(false)

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true)
            setResults([])

            const brojNaloga = values.brojNaloga?.trim()
            const stavka = values.stavka?.trim()
            const sifra = values.sifraArtikla?.trim()

            let combined: any[] = []

            /**
             * =========================
             * ✅ CASE 1: SIFRA ONLY
             * =========================
             */
            if (sifra && !brojNaloga) {

                const standardRes = await getDostupniDopoviPoSifri(null, null, sifra)

                if (standardRes?.success && standardRes?.data) {
                    const standardArray = Array.isArray(standardRes.data)
                        ? standardRes.data
                        : [standardRes.data]

                    combined = standardArray.map((dop: any) => ({
                        ...dop,
                        izvor: 'standard'
                    }))
                }
            }

            /**
             * =========================
             * ✅ CASE 2: NALOG
             * =========================
             */
            if (brojNaloga) {

                const [standardRes, privremeniRes] = await Promise.all([
                    getDostupniDopoviPoSifri(brojNaloga, stavka),
                    getDostupniPrivremeniDopovi(brojNaloga, stavka)
                ])

                // STANDARD
                if (standardRes?.success && standardRes?.data) {
                    const standardArray = Array.isArray(standardRes.data)
                        ? standardRes.data
                        : [standardRes.data]

                    const standard = standardArray.map((dop: any) => ({
                        ...dop,
                        izvor: 'standard'
                    }))

                    combined = [...combined, ...standard]
                }

                // PRIVREMENI
                if (privremeniRes?.success && privremeniRes?.data) {
                    const privremeniArray = Array.isArray(privremeniRes.data)
                        ? privremeniRes.data
                        : [privremeniRes.data]

                    const privremeni = privremeniArray.map((dop: any) => ({
                        ...dop,
                        izvor: 'privremeni'
                    }))

                    combined = [...combined, ...privremeni]
                }
            }

            // remove duplicates
            const unique = Array.from(
                new Map(combined.map((item) => [item.Id, item])).values()
            )

            // sort privremeni first
            unique.sort((a, b) =>
                a.izvor === b.izvor ? 0 : a.izvor === 'privremeni' ? -1 : 1
            )

            setResults(unique)

        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header
                closeButton
                className="border-0 pb-0"
            >
                <div className="d-flex align-items-center">
                    <div className="symbol symbol-45px me-4">
                        <div className="symbol-label bg-light">
                            <KTIcon
                                iconName="file-sheet"
                                className="fs-2 text-gray-700"
                            />
                        </div>
                    </div>

                    <div>
                        <h2 className="fs-2 fw-bolder text-gray-900 mb-1">
                            DOP Izvještaji
                        </h2>

                        <div className="text-muted fs-7">
                            Pretraga i generisanje DOP dokumentacije
                        </div>
                    </div>
                </div>
            </Modal.Header>

            <Formik
                initialValues={{
                    brojNaloga: '',
                    stavka: '',
                    sifraArtikla: ''
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({errors, isSubmitting, values}) => (
                    <Form>

                        <Modal.Body className="pt-8">

                            {/* INFO BOX */}
                            <div className="notice d-flex bg-light rounded border border-gray-200 p-5 mb-10">

                                <KTIcon
                                    iconName="information-5"
                                    className="fs-2hx text-gray-500 me-5"
                                />

                                <div className="d-flex flex-column">
                                    <h4 className="text-gray-900 fw-bold mb-2">
                                        Način pretrage
                                    </h4>

                                    <div className="text-gray-700 fs-6 lh-lg">
                                        Dokument možete pronaći koristeći:
                                    </div>

                                    <div className="d-flex gap-2 mt-4 flex-wrap">

                                <span className="badge badge-secondary px-4 py-3">
                                    Šifra artikla
                                </span>

                                        <span className="text-muted align-self-center">
                                    ili
                                </span>

                                        <span className="badge badge-secondary px-4 py-3">
                                    Broj naloga + stavka
                                </span>

                                    </div>
                                </div>
                            </div>

                            {/* SEARCH SECTION */}
                            <div className="card border border-gray-200 shadow-sm">

                                <div className="card-header border-0 pt-6">
                                    <h3 className="card-title fw-bold text-gray-800">
                                        Parametri
                                    </h3>
                                </div>

                                <div className="card-body pt-2">

                                    <div className="row g-6">

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-gray-700">
                                                Broj naloga
                                            </label>

                                            <Field
                                                name="brojNaloga"
                                                className="form-control form-control-solid"
                                                placeholder="Unesite broj naloga"
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-gray-700">
                                                Stavka
                                            </label>

                                            <Field
                                                name="stavka"
                                                className="form-control form-control-solid"
                                                placeholder="Unesite stavku"
                                            />
                                        </div>

                                        <div className="col-12">
                                            <div className="separator separator-dashed my-2"></div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-gray-700">
                                                Šifra artikla
                                            </label>

                                            <Field
                                                name="sifraArtikla"
                                                className="form-control form-control-solid"
                                                placeholder="Unesite šifru artikla"
                                            />
                                        </div>

                                    </div>

                                    {errors && typeof errors === 'string' && (
                                        <div className="text-danger fs-7 mt-4">
                                            {errors}
                                        </div>
                                    )}

                                </div>
                            </div>

                            {/* LOADING */}
                            {loading && (
                                <div className="d-flex justify-content-center py-15">
                                    <div className="spinner-border text-primary" />
                                </div>
                            )}

                            {/* RESULTS */}
                            {results.length > 0 && (
                                <div className="mt-10">

                                    <div className="d-flex align-items-center justify-content-between mb-5">

                                        <div>
                                            <h3 className="fw-bold text-gray-900 mb-1">
                                                Pronađeni dokumenti
                                            </h3>

                                            <div className="text-muted fs-7">
                                                Ukupno pronađeno: {results.length}
                                            </div>
                                        </div>

                                    </div>

                                    <div className="d-flex flex-column gap-5">

                                        {results.map((dop) => (

                                            <div
                                                key={dop.id}
                                                className="card border border-gray-200 shadow-sm"
                                            >

                                                <div className="card-body p-6">

                                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-8">

                                                        {/* LEFT */}
                                                        <div className="d-flex align-items-center">

                                                            <div className="symbol symbol-55px me-5">
                                                                <div className="symbol-label bg-light">
                                                                    <KTIcon
                                                                        iconName="abstract-26"
                                                                        className="fs-2 text-gray-700"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div>

                                                                <div className="d-flex align-items-center gap-3 mb-2">
                                                                    <h4 className="fw-bolder text-gray-900 mb-0">
                                                                        DOP # {dop.id}
                                                                    </h4>
                                                                    <span
                                                                        className={`badge ${
                                                                            dop.izvor === 'privremeni'
                                                                                ? 'badge-light-warning'
                                                                                : 'badge-light-success'
                                                                        }`}
                                                                        >
                                                                    {dop.izvor === 'privremeni'
                                                                        ? 'Privremeni'
                                                                        : 'Standardni'}
                                                                    </span>

                                                                </div>
                                                                <div className="text-muted fs-7 mb-3">
                                                                    {dop.standard}
                                                                </div>
                                                                <div className="text-muted fs-7 mb-3">
                                                                    {dop.izvor_mehanickih_svojstava}
                                                                </div>

                                                                <div className="d-flex flex-wrap gap-2">

                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span className="text-muted fs-7">
                                                                            Sistem:
                                                                        </span>

                                                                        <span className="fw-bold text-gray-900 fs-7">
                                                                            {dop.sistem}
                                                                        </span>
                                                                    </div>

                                                                </div>

                                                            </div>

                                                        </div>

                                                        {/* RIGHT */}
                                                        <div className="text-end">

                                                            <div className="text-muted fs-8 fw-semibold mb-3">
                                                                Generiši PDF dokument
                                                            </div>

                                                            <DopLanguageButtons
                                                                onSelect={(langKey) => {

                                                                    let url = `../digitalni-dop-wizzard-pdf/${dop.id}?lang=${langKey}`

                                                                    if (values.brojNaloga) {
                                                                        url += `&brojNaloga=${encodeURIComponent(values.brojNaloga)}`
                                                                    }

                                                                    if (values.stavka) {
                                                                        url += `&stavka=${encodeURIComponent(values.stavka)}`
                                                                    }

                                                                    if (values.sifraArtikla) {
                                                                        url += `&sifraArtikla=${encodeURIComponent(values.sifraArtikla)}`
                                                                    }

                                                                    window.open(url, '_blank')
                                                                }}
                                                            />

                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                        ))}

                                    </div>

                                </div>
                            )}

                        </Modal.Body>

                        <Modal.Footer className="border-0 pt-0">

                            <Button
                                variant="light"
                                onClick={onClose}
                            >
                                Zatvori
                            </Button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                <KTIcon
                                    iconName="magnifier"
                                    className="fs-4 me-2"
                                />

                                Prikaži dokumente
                            </button>

                        </Modal.Footer>

                    </Form>
                )}
            </Formik>
        </Modal>
    )
}

export default IzvjestajiModal